import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import { canViewLocalOpsOps } from '../../utils/localOpsOpsAccess';
import './advertiseOnboarding.css';

const SURFACES = [
  ['feed', 'Feed'],
  ['rightbar', 'Right rail'],
  ['chat', 'Chat'],
  ['profile', 'Profile'],
  ['profile_video', 'Profile video'],
  ['advertise', 'Advertise'],
];

const PLAN_OPTIONS = [
  ['monthly', 'Monthly'],
  ['six_month', 'Six months'],
  ['annual', 'Annual'],
];

const CREATIVE_PREFERENCES = [
  ['ready_made', 'Ready-made creative'],
  ['create_for_me', 'Create for me'],
];

const getDefaultManualForm = () => ({
  planKey: 'monthly',
  businessName: '',
  contactName: '',
  emailAddress: '',
  phoneNumber: '',
  businessCategory: '',
  businessWebsiteOrSocialUrl: '',
  destinationUrl: '',
  requestHelpWithDestination: false,
  creativePreference: 'ready_made',
  creativeImageUrl: '',
  mainOfferOrCallToAction: '',
  brandNotes: '',
  salespersonCode: '',
  manualPaymentReference: '',
  termsAccepted: false,
  assetAuthorizationAccepted: false,
});

const createManualIdempotencyKey = () =>
  `manual-check-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const getMessage = (error) =>
  error?.response?.data?.message || 'The Spotlight request failed.';

const formatDate = (value) =>
  value ? new Date(value).toLocaleString() : 'Not recorded';

const prioritizeAccounts = (accounts) =>
  [...accounts].sort((left, right) => {
    const priority = (account) =>
      account.onboardingStatus === 'paid_pending_creative' ? 0 : 1;
    return (
      priority(left) - priority(right) ||
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    );
  });

export default function AdvertiseOnboarding() {
  const { player } = useContext(AuthContext);
  const allowed = canViewLocalOpsOps(player);
  const token = player?.token;
  const [accounts, setAccounts] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);
  const [creative, setCreative] = useState({
    finalHeadline: '',
    finalDestinationUrl: '',
    finalCreativeUrl: '',
    approvedSurfaceKeys: ['feed', 'rightbar'],
  });
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState('');
  const [manualOpen, setManualOpen] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);
  const [manualForm, setManualForm] = useState(getDefaultManualForm);
  const [manualIdempotencyKey, setManualIdempotencyKey] = useState(
    createManualIdempotencyKey,
  );
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const authConfig = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token],
  );

  const loadAccounts = useCallback(async () => {
    if (!allowed || !token) return;
    setLoading(true);
    setError('');
    try {
      const response = await axiosConn.get(
        'local-business-spotlight/admin/accounts',
        authConfig,
      );
      const nextAccounts = prioritizeAccounts(
        response.data?.data?.accounts || [],
      );
      setAccounts(nextAccounts);
      setSelectedId((current) => current || nextAccounts[0]?._id || '');
    } catch (requestError) {
      setError(getMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [allowed, authConfig, token]);

  const loadDetail = useCallback(async () => {
    if (!allowed || !token || !selectedId) {
      setDetail(null);
      return;
    }
    setError('');
    try {
      const response = await axiosConn.get(
        `local-business-spotlight/admin/accounts/${selectedId}`,
        authConfig,
      );
      const nextDetail = response.data?.data || null;
      const account = nextDetail?.account;
      setDetail(nextDetail);
      setCreative({
        finalHeadline: account?.finalHeadline || account?.adHeadline || '',
        finalDestinationUrl:
          account?.finalDestinationUrl || account?.destinationUrl || '',
        finalCreativeUrl:
          account?.finalCreativeUrl || account?.creativeImageUrl || '',
        approvedSurfaceKeys:
          account?.approvedSurfaceKeys?.length > 0
            ? account.approvedSurfaceKeys
            : ['feed', 'rightbar'],
      });
    } catch (requestError) {
      setError(getMessage(requestError));
    }
  }, [allowed, authConfig, selectedId, token]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  if (!allowed) {
    return (
      <main className="spotlight-admin spotlight-admin--locked">
        <h1>Advertise Onboarding</h1>
        <p>Exact administrator access is required.</p>
      </main>
    );
  }

  const updateCreative = (event) => {
    const { name, value } = event.target;
    setCreative((current) => ({ ...current, [name]: value }));
  };

  const updateManualForm = (event) => {
    const { checked, name, type, value } = event.target;
    setManualForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const toggleSurface = (surfaceKey) => {
    setCreative((current) => {
      const hasSurface = current.approvedSurfaceKeys.includes(surfaceKey);
      return {
        ...current,
        approvedSurfaceKeys: hasSurface
          ? current.approvedSurfaceKeys.filter((key) => key !== surfaceKey)
          : [...current.approvedSurfaceKeys, surfaceKey],
      };
    });
  };

  const runAction = async (action) => {
    if (!detail?.account?._id || busyAction) return;
    setBusyAction(action);
    setError('');
    setNotice('');
    try {
      if (action === 'save') {
        await axiosConn.patch(
          `local-business-spotlight/admin/accounts/${detail.account._id}/creative`,
          creative,
          authConfig,
        );
      } else {
        await axiosConn.post(
          `local-business-spotlight/admin/accounts/${detail.account._id}/${action}`,
          action === 'publish' ? creative : {},
          authConfig,
        );
      }
      setNotice(
        action === 'save'
          ? 'Creative saved.'
          : `Spotlight account ${action} completed.`,
      );
      await Promise.all([loadAccounts(), loadDetail()]);
    } catch (requestError) {
      setError(getMessage(requestError));
    } finally {
      setBusyAction('');
    }
  };

  const submitManualCheckAccount = async (event) => {
    event.preventDefault();
    if (manualBusy) return;
    setManualBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await axiosConn.post(
        'local-business-spotlight/admin/accounts/manual-check',
        {
          ...manualForm,
          termsAccepted: manualForm.termsAccepted === true,
          assetAuthorizationAccepted:
            manualForm.assetAuthorizationAccepted === true,
        },
        {
          headers: {
            ...authConfig.headers,
            'Idempotency-Key': manualIdempotencyKey,
          },
        },
      );
      const createdAccount = response.data?.data?.account || null;
      if (createdAccount?._id) {
        setAccounts((current) =>
          prioritizeAccounts([
            createdAccount,
            ...current.filter((item) => item._id !== createdAccount._id),
          ]),
        );
        setSelectedId(createdAccount._id);
      }
      setNotice(
        response.data?.data?.reused
          ? 'Check-paid Spotlight customer loaded.'
          : 'Check-paid Spotlight customer created.',
      );
      setManualForm(getDefaultManualForm());
      setManualIdempotencyKey(createManualIdempotencyKey());
      await loadAccounts();
    } catch (requestError) {
      setError(getMessage(requestError));
    } finally {
      setManualBusy(false);
    }
  };

  const account = detail?.account;
  const metrics = detail?.metrics;
  const isVideo = /\.(mp4|webm|mov|m4v)(\?|$)/i.test(
    creative.finalCreativeUrl,
  );

  return (
    <main className="spotlight-admin">
      <Helmet>
        <title>Advertise Onboarding | JG LocalOps</title>
      </Helmet>
      <header className="spotlight-admin-header">
        <div>
          <p>JG LOCAL BUSINESS SPOTLIGHT</p>
          <h1>Advertise Onboarding</h1>
          <span>Paid submissions first. Review, preview and publish.</span>
        </div>
        <button type="button" onClick={loadAccounts} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </header>

      {error ? <div className="spotlight-admin-alert">{error}</div> : null}
      {notice ? (
        <div className="spotlight-admin-alert spotlight-admin-alert--success">
          {notice}
        </div>
      ) : null}

      <section className="spotlight-admin-manual">
        <button
          type="button"
          className="spotlight-admin-manual-toggle"
          onClick={() => setManualOpen((current) => !current)}
          aria-expanded={manualOpen}
        >
          <span>
            <strong>Add check-paid customer</strong>
            <small>Creates a paid pending creative account for review. Does not publish.</small>
          </span>
          <span>{manualOpen ? 'Hide' : 'Open'}</span>
        </button>

        {manualOpen ? (
          <form
            className="spotlight-admin-manual-form"
            onSubmit={submitManualCheckAccount}
          >
            <label>
              Plan
              <select
                name="planKey"
                value={manualForm.planKey}
                onChange={updateManualForm}
              >
                {PLAN_OPTIONS.map(([value, label]) => (
                  <option value={value} key={value}>{label}</option>
                ))}
              </select>
            </label>
            <label>
              Business name
              <input
                name="businessName"
                value={manualForm.businessName}
                onChange={updateManualForm}
                required
              />
            </label>
            <label>
              Contact name
              <input
                name="contactName"
                value={manualForm.contactName}
                onChange={updateManualForm}
                required
              />
            </label>
            <label>
              Email
              <input
                name="emailAddress"
                type="email"
                value={manualForm.emailAddress}
                onChange={updateManualForm}
                required
              />
            </label>
            <label>
              Phone
              <input
                name="phoneNumber"
                value={manualForm.phoneNumber}
                onChange={updateManualForm}
              />
            </label>
            <label>
              Business category
              <input
                name="businessCategory"
                value={manualForm.businessCategory}
                onChange={updateManualForm}
                required
              />
            </label>
            <label>
              Business website or social URL
              <input
                name="businessWebsiteOrSocialUrl"
                value={manualForm.businessWebsiteOrSocialUrl}
                onChange={updateManualForm}
                placeholder="https://"
                required
              />
            </label>
            <label>
              Destination URL
              <input
                name="destinationUrl"
                value={manualForm.destinationUrl}
                onChange={updateManualForm}
                placeholder="https://"
                required={!manualForm.requestHelpWithDestination}
              />
            </label>
            <label className="spotlight-admin-manual-check">
              <input
                name="requestHelpWithDestination"
                type="checkbox"
                checked={manualForm.requestHelpWithDestination}
                onChange={updateManualForm}
              />
              Needs destination help
            </label>
            <label>
              Creative preference
              <select
                name="creativePreference"
                value={manualForm.creativePreference}
                onChange={updateManualForm}
              >
                {CREATIVE_PREFERENCES.map(([value, label]) => (
                  <option value={value} key={value}>{label}</option>
                ))}
              </select>
            </label>
            <label>
              Creative/image URL
              <input
                name="creativeImageUrl"
                value={manualForm.creativeImageUrl}
                onChange={updateManualForm}
                placeholder="https://"
                required={manualForm.creativePreference === 'ready_made'}
              />
            </label>
            <label>
              Salesperson code
              <input
                name="salespersonCode"
                value={manualForm.salespersonCode}
                onChange={updateManualForm}
              />
            </label>
            <label>
              Check/reference note
              <input
                name="manualPaymentReference"
                value={manualForm.manualPaymentReference}
                onChange={updateManualForm}
              />
            </label>
            <label className="spotlight-admin-manual-wide">
              Offer / CTA
              <textarea
                name="mainOfferOrCallToAction"
                value={manualForm.mainOfferOrCallToAction}
                onChange={updateManualForm}
                required
              />
            </label>
            <label className="spotlight-admin-manual-wide">
              Brand notes
              <textarea
                name="brandNotes"
                value={manualForm.brandNotes}
                onChange={updateManualForm}
              />
            </label>
            <label className="spotlight-admin-manual-check spotlight-admin-manual-wide">
              <input
                name="termsAccepted"
                type="checkbox"
                checked={manualForm.termsAccepted}
                onChange={updateManualForm}
                required
              />
              Customer accepted Spotlight terms
            </label>
            <label className="spotlight-admin-manual-check spotlight-admin-manual-wide">
              <input
                name="assetAuthorizationAccepted"
                type="checkbox"
                checked={manualForm.assetAuthorizationAccepted}
                onChange={updateManualForm}
                required
              />
              Customer granted asset authorization
            </label>
            <div className="spotlight-admin-manual-actions">
              <button type="submit" disabled={manualBusy}>
                {manualBusy ? 'Creating...' : 'Create check-paid customer'}
              </button>
            </div>
          </form>
        ) : null}
      </section>

      <div className="spotlight-admin-layout">
        <aside className="spotlight-admin-queue">
          <h2>Queue</h2>
          {accounts.length === 0 && !loading ? (
            <p>No Spotlight onboarding records.</p>
          ) : null}
          {accounts.map((item) => (
            <button
              type="button"
              className={
                selectedId === item._id
                  ? 'spotlight-admin-account spotlight-admin-account--active'
                  : 'spotlight-admin-account'
              }
              key={item._id}
              onClick={() => setSelectedId(item._id)}
            >
              <strong>{item.businessName}</strong>
              <span>{item.onboardingStatus}</span>
              <small>
                {item.planKey} &bull; {item.billingStatus}
              </small>
            </button>
          ))}
        </aside>

        <section className="spotlight-admin-detail">
          {!account ? (
            <p>Select a Spotlight onboarding record.</p>
          ) : (
            <>
              <div className="spotlight-admin-facts">
                <div><small>Business</small><strong>{account.businessName}</strong></div>
                <div><small>Contact</small><strong>{account.contactName}</strong></div>
                <div><small>Email</small><strong>{account.emailAddress}</strong></div>
                <div><small>Phone</small><strong>{account.phoneNumber || 'Not supplied'}</strong></div>
                <div><small>Plan</small><strong>{account.planKey}</strong></div>
                <div><small>Billing</small><strong>{account.billingStatus}</strong></div>
                <div><small>Paid</small><strong>{formatDate(account.paidAt)}</strong></div>
                <div><small>Period end</small><strong>{formatDate(account.currentPeriodEnd)}</strong></div>
                <div><small>Capacity</small><strong>{account.capacityStatus}</strong></div>
                <div><small>Salesperson</small><strong>{account.salespersonCode || 'Not attributed'}</strong></div>
              </div>

              <div className="spotlight-admin-source">
                <h2>Submitted source material</h2>
                <p><strong>Category:</strong> {account.businessCategory}</p>
                <p><strong>Offer / CTA:</strong> {account.mainOfferOrCallToAction}</p>
                <p><strong>Brand notes:</strong> {account.brandNotes || 'None'}</p>
                <p><strong>Creative preference:</strong> {account.creativePreference}</p>
                {account.manualPaymentMethod ? (
                  <p><strong>Manual payment:</strong> {account.manualPaymentReference || account.manualPaymentMethod}</p>
                ) : null}
                <div className="spotlight-admin-links">
                  {[account.businessWebsiteOrSocialUrl, account.destinationUrl, account.logoUrl, account.creativeImageUrl, ...(account.additionalAssetUrls || [])]
                    .filter(Boolean)
                    .map((url) => (
                      <a href={url} target="_blank" rel="noreferrer" key={url}>
                        Open submitted link
                      </a>
                    ))}
                </div>
              </div>

              <div className="spotlight-admin-editor">
                <label>
                  Final headline
                  <input name="finalHeadline" value={creative.finalHeadline} onChange={updateCreative} />
                </label>
                <label>
                  Final destination URL
                  <input name="finalDestinationUrl" value={creative.finalDestinationUrl} onChange={updateCreative} placeholder="https://" />
                </label>
                <label>
                  Final creative image or video URL
                  <input name="finalCreativeUrl" value={creative.finalCreativeUrl} onChange={updateCreative} placeholder="https://" />
                </label>
                <fieldset>
                  <legend>Approved surfaces</legend>
                  {SURFACES.map(([key, label]) => (
                    <label key={key}>
                      <input type="checkbox" checked={creative.approvedSurfaceKeys.includes(key)} onChange={() => toggleSurface(key)} />
                      {label}
                    </label>
                  ))}
                </fieldset>
              </div>

              <div className="spotlight-admin-preview">
                <span>PREVIEW</span>
                {creative.finalCreativeUrl ? (
                  isVideo ? (
                    <video src={creative.finalCreativeUrl} controls muted />
                  ) : (
                    <img src={creative.finalCreativeUrl} alt="" />
                  )
                ) : (
                  <div className="spotlight-admin-preview-empty">Add a creative URL.</div>
                )}
                <strong>Ad Sponsored by {account.businessName}</strong>
                <h2>{creative.finalHeadline || 'Final headline preview'}</h2>
              </div>

              <div className="spotlight-admin-metrics">
                <div><small>Recorded displays</small><strong>{metrics?.impressions || 0}</strong></div>
                <div><small>Recorded clicks</small><strong>{metrics?.clicks || 0}</strong></div>
                <div><small>Measurement began</small><strong>{formatDate(account.measurementStartedAt)}</strong></div>
              </div>

              <div className="spotlight-admin-actions">
                <button type="button" onClick={() => runAction('save')} disabled={Boolean(busyAction)}>Save creative</button>
                <button type="button" onClick={() => runAction('publish')} disabled={Boolean(busyAction)}>Publish</button>
                <button type="button" onClick={() => runAction('pause')} disabled={Boolean(busyAction)}>Pause</button>
                <button type="button" onClick={() => runAction('resume')} disabled={Boolean(busyAction)}>Resume</button>
              </div>
            </>
          )}
        </section>
      </div>
    </main>
  );
}
